import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from './Avatar';
import { useFixedAnchoredPanel } from './useFixedAnchoredPanel';

/**
 * Typeahead user picker. Mounted in 14.3c as the `user`-kind custom-attribute
 * control on the document editor inspector. Renders an `<input>` filter over
 * the supplied option list (typically `assigneeOptions` from
 * `useDocumentEditorVM`); on selection the picker collapses into a chip
 * showing the picked user's avatar + display name with an × clear affordance.
 *
 * Value shape: `{ user_id: string } | null`. The picker is value-agnostic
 * beyond resolving the matching `UserPickerOption` from `options` — callers
 * own the persisted shape (it stays a jsonb `{user_id}` object so
 * `custom_attribute_value_versions.value` can carry it through without a
 * column-shape mismatch — see 14.1 schema notes).
 */

export interface UserPickerOption {
  user_id: string;
  display_name: string;
  username?: string | null;
}

export interface UserPickerProps {
  id: string;
  label: string;
  options: UserPickerOption[];
  value: { user_id: string } | null;
  onPick: (next: UserPickerOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  triggerVariant?: 'default' | 'pill';
  pillLabel?: string;
  pillMarker?: ReactNode;
  pillMarkerTestId?: string;
  pillMuted?: boolean;
  'data-testid'?: string;
}

export function UserPicker({
  id,
  label,
  options,
  value,
  onPick,
  disabled,
  placeholder = 'Search members…',
  className,
  triggerVariant = 'default',
  pillLabel,
  pillMarker,
  pillMarkerTestId,
  pillMuted,
  ...rest
}: UserPickerProps) {
  const testId = rest['data-testid'];
  const pillRef = useRef<HTMLButtonElement>(null);
  const selected = value
    ? options.find((o) => o.user_id === value.user_id) ?? null
    : null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => o.display_name.toLowerCase().includes(needle))
    : options;

  const closePanel = () => {
    setOpen(false);
    setQuery('');
    setHighlighted(0);
  };

  const { panelRef, style: panelStyle } = useFixedAnchoredPanel(
    pillRef,
    open && triggerVariant === 'pill',
    closePanel,
    'start'
  );

  useEffect(() => {
    if (!open || triggerVariant === 'pill') return;
    const onDocPointer = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open, triggerVariant]);

  const commit = (opt: UserPickerOption) => {
    onPick(opt);
    setQuery('');
    setHighlighted(0);
    setOpen(false);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      const pick = filtered[highlighted];
      if (pick) {
        e.preventDefault();
        commit(pick);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  };

  if (triggerVariant === 'pill') {
    const pillCls = [
      'pn-property-pill',
      pillMuted ? 'pn-property-pill-muted' : null
    ]
      .filter(Boolean)
      .join(' ');
    const openPicker = () => {
      if (disabled) return;
      setQuery('');
      setHighlighted(0);
      setOpen(true);
    };
    const panel = open ? (
      <div
        ref={panelRef}
        className="pn-userpicker-panel pn-popover-fixed"
        style={panelStyle}
        role="dialog"
        aria-label={label}
        data-testid={testId ? `${testId}-panel` : undefined}
      >
        <input
          type="text"
          className="pn-field-input pn-userpicker-input"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          role="combobox"
          aria-expanded
          aria-controls={`${id}-listbox`}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <ul
          id={`${id}-listbox`}
          className="pn-userpicker-list pn-userpicker-list-portal"
          role="listbox"
        >
          {filtered.length > 0 ? (
            filtered.map((opt, idx) => (
              <li
                key={opt.user_id}
                role="option"
                aria-selected={idx === highlighted}
                className={[
                  'pn-userpicker-option',
                  idx === highlighted ? 'pn-userpicker-option-active' : null
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                data-testid={
                  testId ? `${testId}-option-${opt.user_id}` : undefined
                }
              >
                <Avatar name={opt.display_name} size="sm" />
                <span className="pn-userpicker-option-name">
                  {opt.display_name}
                </span>
              </li>
            ))
          ) : (
            <li className="pn-userpicker-empty" aria-disabled="true">
              No members match
            </li>
          )}
        </ul>
      </div>
    ) : null;

    return (
      <>
        <button
          ref={pillRef}
          type="button"
          className={pillCls}
          disabled={disabled}
          onClick={openPicker}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={testId}
        >
          {pillLabel}
          {pillMarker ? (
            <>
              {' '}
              <span
                className="pn-property-pill-marker"
                data-testid={pillMarkerTestId}
                aria-hidden="true"
              >
                {pillMarker}
              </span>
            </>
          ) : null}
        </button>
        {panel ? createPortal(panel, document.body) : null}
      </>
    );
  }

  // Collapsed (selected) state — render the chip + × clear.
  if (selected && !open) {
    const wrapperClass = [
      'pn-userpicker',
      'pn-userpicker-collapsed',
      className
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        ref={containerRef}
        className={wrapperClass}
        data-testid={testId}
        data-state="collapsed"
      >
        <label htmlFor={id} className="pn-field-label pn-field-label-hidden">
          {label}
        </label>
        <button
          type="button"
          id={id}
          className="pn-userpicker-chip"
          disabled={disabled}
          onClick={() => {
            setQuery('');
            setHighlighted(0);
            setOpen(true);
          }}
          data-testid={testId ? `${testId}-chip` : undefined}
        >
          <Avatar name={selected.display_name} size="sm" />
          <span className="pn-userpicker-chip-name">
            {selected.display_name}
          </span>
        </button>
        <button
          type="button"
          className="pn-userpicker-clear"
          aria-label={`Clear ${label}`}
          disabled={disabled}
          onClick={() => onPick(null)}
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          ×
        </button>
      </div>
    );
  }

  const wrapperClass = ['pn-userpicker', className].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={wrapperClass}
      data-testid={testId}
      data-state="open"
    >
      <label htmlFor={id} className="pn-field-label pn-field-label-hidden">
        {label}
      </label>
      <input
        type="text"
        id={id}
        className="pn-field-input pn-userpicker-input"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {open && filtered.length > 0 ? (
        <ul
          id={`${id}-listbox`}
          className="pn-userpicker-list"
          role="listbox"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {filtered.map((opt, idx) => (
            <li
              key={opt.user_id}
              role="option"
              aria-selected={idx === highlighted}
              className={[
                'pn-userpicker-option',
                idx === highlighted ? 'pn-userpicker-option-active' : null
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseDown={(e) => {
                // mousedown beats the input's blur so the click survives.
                e.preventDefault();
                commit(opt);
              }}
              onMouseEnter={() => setHighlighted(idx)}
              data-testid={
                testId ? `${testId}-option-${opt.user_id}` : undefined
              }
            >
              <Avatar name={opt.display_name} size="sm" />
              <span className="pn-userpicker-option-name">
                {opt.display_name}
              </span>
            </li>
          ))}
        </ul>
      ) : open ? (
        <ul
          id={`${id}-listbox`}
          className="pn-userpicker-list pn-userpicker-list-empty"
          role="listbox"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          <li className="pn-userpicker-empty" aria-disabled="true">
            No members match
          </li>
        </ul>
      ) : null}
    </div>
  );
}
